import { AccordionDetails, CSSObject, styled, Theme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { match } from 'ts-pattern';

import {
  calizaApi as api,
  useCreateTransactionMutation,
  useGetBitsoBanksByCountryCodeQuery,
  useGetConfigurationQuery,
  useGetContactsQuery,
  useGetDashboardOperationsQuery,
} from '@/api/v1';
import ExpandIcon from '@/assets/icons/dropdown-arrow.svg?react';
import { AddObjectButton } from '@/components/molecules/AddObjectButton';
import { ExpectedSettlementTimeInfo } from '@/components/molecules/ExpectedSettlementTimeInfo';
import { FullScreenModalContainer } from '@/components/molecules/FullScreenModalContainer';
import { FundingMethodSelect } from '@/components/molecules/FundingMethodSelect';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { OutsideBRLMarketHoursAlert } from '@/components/molecules/OutsideBRLMarketHoursAlert';
import { RecipientsSelect } from '@/components/molecules/RecipientsSelect';
import { AmountAndCurrencyWidget } from '@/components/organisms/AmountAndCurrencyWidget';
import { ApplicationDialog } from '@/components/organisms/ApplicationDialog';
import { Header } from '@/components/organisms/Header';
import { TransactionDetails } from '@/components/organisms/TransactionDetails';
import { useBalance } from '@/hooks/useBalance';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useCustomer } from '@/hooks/useCustomer';
import { nullifyCalizaWallet, useSimulation } from '@/hooks/useSimulation';
import { useToast } from '@/hooks/useToast';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { DocumentSections } from '@/Screens/Dashboard/Flows/TransferFlow/DocumentSections';
import {
  fromAmountChanged,
  fundingMethodChanged,
  initialState,
  paymentReferenceChanged,
  recipientAccountChanged,
  simulationCreated,
  toAmountChanged,
  transactionFlowChanged,
  TransferFlowAction,
  transferFlowReducer,
  TransferFlowState,
  transferPurposeChanged,
} from '@/Screens/Dashboard/Flows/TransferFlow/reducer';
import { Section } from '@/Screens/Dashboard/Flows/TransferFlow/Section';
import { Amount } from '@/types/amount';
import { DetailedBalance } from '@/types/bankInfo';
import { BitsoBank } from '@/types/bitsoBank';
import {
  AccountDetails,
  Contact,
  FiatAccount,
  RecipientAccount,
  SwiftAccountDetails,
} from '@/types/contacts';
import { isSwiftAccountDetails } from '@/types/contacts.ext';
import { CountryType } from '@/types/country';
import { isCountryType } from '@/types/country.ext';
import {
  CurrencyCodeType,
  DashboardOperation,
  DashboardOperationDestination,
  FundingMethodItem,
  PaymentRailType,
} from '@/types/currency';
import { FiatAccountType } from '@/types/fiatAccountType';
import { Locale } from '@/types/locale';
import { NotNullable } from '@/types/notNullable';
import { NfTransactionDetailsResponse } from '@/types/simulation';
import { MarketExecutionType, TransactionResponse } from '@/types/transaction';
import { isBRLOperationOutsideMarketHours } from '@/utility/brlMarketHours';
import { checkStringValidSwiftCharacters } from '@/utility/checkStringValidSwiftCharacters';
import { getAccountCurrency } from '@/utility/fiatAccounts';
import { AsAnyActionArg } from '@/utility/reducers';

enum FormErrors {
  MISSING_ACCOUNT = 'MISSING_ACCOUNT',
  MISSING_FUNDING_METHOD = 'MISSING_FUNDING_METHOD',
  MISSING_AMOUNT = 'MISSING_AMOUNT',
  PAYMENT_REFERENCE_INVALID_CHARACTERS = 'PAYMENT_REFERENCE_INVALID_CHARACTERS',
  MISSING_SUPPORTING_DOCUMENTATION = 'MISSING_SUPPORTING_DOCUMENTATION',
  AMOUNT_BIGGER_THAN_BALANCE = 'AMOUNT_BIGGER_THAN_BALANCE',
}

export const TransferFlow: React.FC = (): React.ReactElement => {
  const reducer = React.useReducer<TransferFlowState, AsAnyActionArg<TransferFlowAction>>(
    transferFlowReducer,
    initialState,
  );
  const [state, dispatch] = reducer;

  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const tr = useTranslator();
  const globalDispatch = useDispatch();
  const formatter = useCurrencyFormatter();
  const locale = React.useContext<Locale>(LocaleContext);

  const [error, setError] = React.useState<FormErrors | null>(null);

  const customer = useCustomer();
  const { data: combinedContacts = [], isFetching: loadingContacts } = useGetContactsQuery(
    customer.beneficiaryId,
  );
  const { data: operations = [] } = useGetDashboardOperationsQuery('TRANSFER');
  const { data: colombianBanks = [] } = useGetBitsoBanksByCountryCodeQuery(CountryType.CO);
  const {
    selectedOperation,
    fundingMethod,
    fromAmount,
    toAmount,
    recipientAccountId,
    paymentReference,
    transferPurpose,
  } = state;

  const usdcBalance = useBalance(CurrencyCodeType.USDC);
  const usdtBalance = useBalance(CurrencyCodeType.USDT);

  const [createTransaction, mutation] = useCreateTransactionMutation();

  const balances = React.useMemo(
    (): Partial<Record<CurrencyCodeType, DetailedBalance>> => ({
      [CurrencyCodeType.USDC]: usdcBalance,
      [CurrencyCodeType.USDT]: usdtBalance,
    }),
    [usdcBalance, usdtBalance],
  );

  const recentlyAddedAccountId = React.useMemo((): string | null => {
    const { state } = location;
    return state?.accountId ?? null;
  }, [location]);

  const banksByCountry = React.useMemo(
    (): Partial<Record<CountryType, Record<string, string>>> => ({
      [CountryType.CO]: Object.fromEntries(
        colombianBanks.map((bank: BitsoBank): [string, string] => [bank.code, bank.name]),
      ),
    }),
    [colombianBanks],
  );

  const contacts = React.useMemo(
    (): readonly Contact[] =>
      combinedContacts.filter((contact: Contact): boolean => !contact.belongsToBeneficiary),
    [combinedContacts],
  );

  const destinationCountry = React.useMemo((): CountryType => {
    const accounts =
      contacts
        .map((contact: Contact): readonly FiatAccount[] => {
          return contact.accounts;
        }, [])
        ?.flat() ?? [];
    const selectedAccount = accounts.find(
      (account: FiatAccount): boolean => account.id === recipientAccountId,
    );

    if (!selectedAccount) {
      return CountryType.US;
    }

    switch (selectedAccount.fiatAccountType) {
      case FiatAccountType.ach:
        break;
      case FiatAccountType.wire:
      case FiatAccountType.unset:
        return CountryType.US;
      case FiatAccountType.cop:
        return CountryType.CO;
      case FiatAccountType.spei:
        return CountryType.MX;
      case FiatAccountType.cvu:
        return CountryType.AR;
      case FiatAccountType.pix:
        return CountryType.BR;
      case FiatAccountType.swift:
        return getBankCountryFromAccountDetails(selectedAccount.accountDetails) ?? CountryType.US;
      default:
        return CountryType.US;
    }

    return CountryType.US;
  }, [contacts, recipientAccountId]);

  const fundingMethods = React.useMemo((): readonly FundingMethodItem[] => {
    const currency = getToCurrencyFromAccount(contacts, recipientAccountId);
    const rail = getToRailFromAccount(contacts, recipientAccountId);
    const destinations = operations
      .map(
        (
          operation: DashboardOperation,
        ): readonly (DashboardOperationDestination & {
          readonly fromCurrency: CurrencyCodeType;
        })[] => {
          const { destinations, currency: fromCurrency } = operation;
          return destinations.map(
            (
              destination: DashboardOperationDestination,
            ): DashboardOperationDestination & { readonly fromCurrency: CurrencyCodeType } => ({
              ...destination,
              fromCurrency: fromCurrency,
            }),
          );
        },
      )
      .flat()
      .filter((destination: DashboardOperationDestination): boolean => {
        return destination.currency === currency && destination.rail === rail;
      });

    return destinations
      .map(
        (
          destination: DashboardOperationDestination & { readonly fromCurrency: CurrencyCodeType },
        ): readonly FundingMethodItem[] => {
          const { currency: toCurrency, fromCurrency, rail, fundingMethods } = destination;

          return fundingMethods.map(
            (fundingMethod: PaymentRailType): FundingMethodItem => ({
              id: [rail, fromCurrency, toCurrency, fundingMethod].join('.'),
              currency: fromCurrency,
              method: fundingMethod,
            }),
          );
        },
      )
      .flat();
  }, [contacts, operations, recipientAccountId]);

  const currentDestination = React.useMemo((): DashboardOperationDestination => {
    const currency = getToCurrencyFromAccount(contacts, recipientAccountId);
    const rail = getToRailFromAccount(contacts, recipientAccountId);
    const destinations = operations
      .map(
        (operation: DashboardOperation): readonly DashboardOperationDestination[] =>
          operation.destinations,
      )
      .flat()
      .filter((destination: DashboardOperationDestination): boolean => {
        return destination.rail === rail && destination.currency === currency;
      });

    if (destinations.length == 1) {
      return {
        currency: currency,
        rail: rail,
        fundingMethods: [fundingMethod.method],
      };
    }

    return {
      currency: currency,
      rail: rail,
      fundingMethods: [fundingMethod.method],
    };
  }, [contacts, fundingMethod.method, operations, recipientAccountId]);

  const currentBalance = React.useMemo(
    (): DetailedBalance =>
      balances[fundingMethod.currency] ?? DetailedBalance.zero(fundingMethod.currency),
    [balances, fundingMethod.currency],
  );

  const fromCurrency = React.useMemo(
    (): CurrencyCodeType => selectedOperation?.currency ?? CurrencyCodeType.UNSET,
    [selectedOperation?.currency],
  );

  const [attachment, setAttachment] = React.useState<File | null>(null);
  const [simulation, fetchingSimulation] = useSimulation({
    fromAmount: fromAmount,
    toAmount: toAmount,
    simulationId: null,
    fromCurrency: fromCurrency,
    toCurrency: currentDestination.currency,
    fundingMethod: nullifyCalizaWallet(fundingMethod.method),
    paymentRail: currentDestination.rail,
    destination: recipientAccountId,
  });

  const onClose = React.useCallback((): void => {
    navigate('/home', { replace: true });
  }, [navigate]);

  const toCurrencies = React.useMemo((): readonly CurrencyCodeType[] => {
    return [currentDestination.currency];
  }, [currentDestination]);

  const fromCurrencies = React.useMemo((): readonly CurrencyCodeType[] => {
    return [];
  }, []);

  const isDocumentRequired = React.useMemo((): boolean => {
    return [CurrencyCodeType.USD, CurrencyCodeType.SGD, CurrencyCodeType.HKD].includes(
      currentDestination.currency,
    );
  }, [currentDestination.currency]);

  const accounts = React.useMemo(
    (): readonly RecipientAccount[] =>
      contacts.reduce(
        (accounts: readonly RecipientAccount[], contact: Contact): readonly RecipientAccount[] => {
          return [...accounts, ...Contact.toRecipientAccounts(locale, banksByCountry, contact)];
        },
        [],
      ) ?? [],
    [banksByCountry, contacts, locale],
  );

  const handleConfirm = React.useCallback((): void => {
    if (!recipientAccountId) {
      return setError(FormErrors.MISSING_ACCOUNT);
    }

    if (paymentReference && !checkStringValidSwiftCharacters(paymentReference)) {
      return setError(FormErrors.PAYMENT_REFERENCE_INVALID_CHARACTERS);
    }

    if (!fundingMethod?.id) {
      return setError(FormErrors.MISSING_FUNDING_METHOD);
    }

    if (!fromAmount) {
      return setError(FormErrors.MISSING_AMOUNT);
    }

    const isWalletAndOverflowsBalance =
      fundingMethod.method === PaymentRailType.CalizaWallet &&
      currentBalance.available < fromAmount;
    if (isWalletAndOverflowsBalance) {
      return setError(FormErrors.AMOUNT_BIGGER_THAN_BALANCE);
    }

    if (isDocumentRequired && !attachment) {
      return setError(FormErrors.MISSING_SUPPORTING_DOCUMENTATION);
    }

    if (simulation.id) {
      if (attachment) {
        createTransaction({
          beneficiaryIp: '',
          simulationId: simulation.id,
          marketExecutionType: MarketExecutionType.executeImmediately,
          attachment: attachment,
          paymentReference: paymentReference,
          transferPurpose: transferPurpose,
        });
      } else {
        createTransaction({
          beneficiaryIp: '',
          simulationId: simulation.id,
          marketExecutionType: MarketExecutionType.executeImmediately,
        });
      }

      // FIXME: this is a workaround, it should be removed eventually
      setTimeout((): void => {
        globalDispatch(
          api.util.invalidateTags([
            {
              type: 'Balances',
              id: fromCurrency,
            },
          ]),
        );
      }, 2000);
    }
  }, [
    recipientAccountId,
    fundingMethod?.id,
    fundingMethod.method,
    fromAmount,
    currentBalance.available,
    isDocumentRequired,
    attachment,
    simulation.id,
    createTransaction,
    paymentReference,
    transferPurpose,
    globalDispatch,
    fromCurrency,
  ]);

  const handleTransactionSuccess = React.useCallback(
    (response: TransactionResponse): void => {
      if (fundingMethod.method !== PaymentRailType.CalizaWallet) {
        navigate(`payment?transactionId=${response.id}`, {
          state: { fundingMethod: fundingMethod.method },
        });
      } else {
        navigate('completed');
      }
    },
    [fundingMethod, navigate],
  );

  React.useEffect((): void => {
    switch (mutation.status) {
      case QueryStatus.fulfilled:
        mutation.reset();
        handleTransactionSuccess(mutation.data);
        break;
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      default:
    }
  }, [mutation, toast, handleTransactionSuccess, navigate, tr]);

  const setRecipientAccount = React.useCallback(
    (accountId: string): void => {
      if (error === FormErrors.MISSING_ACCOUNT) {
        setError(null);
      }

      dispatch(recipientAccountChanged(accountId));
    },
    [error, setError, dispatch],
  );

  const setToAmount = React.useCallback(
    (amount: number | null): void => {
      setError(resetAmountRelatedErrors);
      dispatch(toAmountChanged(amount));
    },
    [setError, dispatch],
  );

  const setFromAmount = React.useCallback(
    (amount: number | null): void => {
      setError(resetAmountRelatedErrors);
      dispatch(fromAmountChanged(amount));
    },
    [setError, dispatch],
  );

  const setFundingMethod = React.useCallback(
    (fundingMethod: FundingMethodItem): void => {
      const transactionFlow = operations.find(
        (operation: DashboardOperation): boolean =>
          operation.currency === fundingMethod.currency &&
          operation.destinations.some((dest: DashboardOperationDestination): boolean =>
            dest.fundingMethods.includes(fundingMethod.method),
          ),
      );

      if (error === FormErrors.MISSING_FUNDING_METHOD) {
        setError(null);
      }

      if (transactionFlow) {
        dispatch(transactionFlowChanged(transactionFlow));
        dispatch(fundingMethodChanged(fundingMethod));
      } else {
        console.debug(`unsupported funding method ${fundingMethod.id}`);
      }
    },
    [dispatch, operations, error, setError],
  );

  const creatingTransaction = React.useMemo(
    (): boolean => mutation.status === QueryStatus.pending,
    [mutation.status],
  );

  const {
    data: configuration,
    isFetching: isFetchingConfiguration,
    isError: isErrorConfiguration,
  } = useGetConfigurationQuery();

  const shouldNotAllowOperation = React.useMemo((): boolean => {
    if (isErrorConfiguration || !configuration?.brazilianMarketHours) {
      return false;
    }
    return (
      isFetchingConfiguration ||
      isBRLOperationOutsideMarketHours(fromCurrency, configuration.brazilianMarketHours)
    );
  }, [
    isErrorConfiguration,
    configuration?.brazilianMarketHours,
    isFetchingConfiguration,
    fromCurrency,
  ]);

  const confirmDisabled = React.useMemo((): boolean => {
    if (!simulation?.id) {
      return true;
    }

    return (
      shouldNotAllowOperation ||
      mutation.status === QueryStatus.pending ||
      mutation.status === QueryStatus.fulfilled
    );
  }, [mutation.status, shouldNotAllowOperation, simulation?.id]);

  const handleFileChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (error === FormErrors.MISSING_SUPPORTING_DOCUMENTATION) {
        setError(null);
      }

      const target = event.target as HTMLInputElement;
      const files = target.files ?? [];

      if (files[0]) {
        setAttachment(files[0]);
      }
    },
    [error, setError],
  );

  const handleFileRemove = React.useCallback((): void => {}, []);
  const handlePurposeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.currentTarget;
      dispatch(transferPurposeChanged(value));
    },
    [dispatch],
  );

  const handlePaymentReferenceChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.currentTarget;
      if (error === FormErrors.PAYMENT_REFERENCE_INVALID_CHARACTERS) {
        setError(null);
      }
      dispatch(paymentReferenceChanged(value));
    },
    [error, setError, dispatch],
  );

  React.useEffect((): void => {
    if (mutation.status === QueryStatus.rejected) {
      setTimeout((): void => {
        mutation.reset();
      }, 1200);
    }
  }, [mutation, setRecipientAccount]);

  React.useEffect((): void => {
    if (recentlyAddedAccountId) {
      setRecipientAccount(recentlyAddedAccountId);
    }
  }, [recentlyAddedAccountId, setRecipientAccount]);

  React.useEffect((): void => {
    if (simulation.to.value !== null && simulation.from.value !== null) {
      dispatch(simulationCreated(simulation));
    }
  }, [dispatch, simulation]);

  const fundsOverflowed = React.useMemo((): boolean => {
    if (fromAmount === null || fundingMethod.method !== PaymentRailType.CalizaWallet) {
      return false;
    }

    const balance = balances[fromCurrency];
    if (!balance) {
      return false;
    }

    return Math.round(100 * balance.available) < Math.round(100 * fromAmount);
  }, [balances, fromAmount, fromCurrency, fundingMethod.method]);

  const amountHelperText = React.useMemo(
    (): string | null =>
      match(error)
        .with(FormErrors.MISSING_AMOUNT, (): string => tr('REQUIRED_AMOUNT'))
        .with(FormErrors.AMOUNT_BIGGER_THAN_BALANCE, (): string =>
          tr(
            'AMOUNT_BIGGER_THAN_BALANCE',
            formatter.format(currentBalance.available, currentBalance.currencyCode),
          ),
        )
        .otherwise((): null => null),
    [currentBalance.available, currentBalance.currencyCode, error, formatter, tr],
  );

  const currentTransactionDetails = React.useMemo((): NfTransactionDetailsResponse => {
    return simulation?.transactionDetails ?? emptyTransactionDetails;
  }, [simulation?.transactionDetails]);

  return (
    <ApplicationDialog fullScreen={true}>
      <Header title="MAKE_A_TRANSFER" onClose={onClose} />
      <FullScreenModalContainer>
        <Section title={tr('RECIPIENT_INFORMATION')} visible={true} working={creatingTransaction}>
          <RecipientsSelect
            name="transferRecipient"
            loading={loadingContacts}
            accounts={accounts}
            value={recipientAccountId}
            onChange={setRecipientAccount}
            error={error === FormErrors.MISSING_ACCOUNT}
            helperText={error === FormErrors.MISSING_ACCOUNT ? tr('REQUIRED_RECIPIENT') : null}
          />
          <Box mt={4}>
            <AddObjectButton label={tr('ADD_NEW_RECIPIENT')} path="recipient" />
          </Box>
        </Section>
        <Section
          title={tr('FUNDING_METHOD')}
          visible={recipientAccountId !== ''}
          working={creatingTransaction}>
          <FundingMethodSelect
            label={tr('SELECT_FUNDING_CURRENCY')}
            balances={balances}
            items={fundingMethods}
            value={fundingMethod.id}
            onChange={setFundingMethod}
            error={error === FormErrors.MISSING_FUNDING_METHOD}
            helperText={
              error === FormErrors.MISSING_FUNDING_METHOD ? tr('REQUIRED_FUNDING_METHOD') : null
            }
          />
        </Section>
        <Section
          title={tr('TRANSACTION_DETAILS')}
          visible={fundingMethod.method !== PaymentRailType.unset}
          working={fetchingSimulation || creatingTransaction}
          spotlight={true}>
          <Box display="flex" flexDirection="column" gap={2}>
            <AmountAndCurrencyWidget
              name="sendAmount"
              size="normal"
              label={tr('YOU_SEND')}
              currency={fromCurrency}
              currencies={fromCurrencies}
              amount={fromAmount}
              autoFocus={true}
              error={fundsOverflowed || !!amountHelperText}
              helperText={amountHelperText}
              onAmountChange={setFromAmount}
            />
            <AmountAndCurrencyWidget
              name="receiveAmount"
              size="normal"
              label={tr('RECIPIENT_GETS')}
              currency={currentDestination.currency}
              currencies={toCurrencies}
              amount={toAmount}
              onAmountChange={setToAmount}
            />
            {fundsOverflowed && (
              <Box textAlign="center">
                <Typography color="error">{tr('YOU_DO_NOT_HAVE_ENOUGH_FUNDS')}</Typography>
              </Box>
            )}
            {shouldNotAllowOperation && <OutsideBRLMarketHoursAlert />}
            <StyledAccordion defaultExpanded={true}>
              <AccordionSummary expandIcon={<ExpandIcon />}>
                <Typography variant="h7">{tr('TRANSACTION_DETAILS_TITLE')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TransactionDetails
                  transactionDetails={currentTransactionDetails}
                  fromAmount={fromAmount}
                  fromCurrency={fromCurrency}
                  toAmount={toAmount}
                  toCurrency={currentDestination.currency}
                  validityTimestamp={simulation.expiresAt}
                />
              </AccordionDetails>
            </StyledAccordion>
          </Box>

          <Box sx={styles.expectedSettlementTimeInfo}>
            <ExpectedSettlementTimeInfo
              expectedSettlementTimeDetails={
                simulation.transactionDetails.expectedSettlementTimeDetails
              }
            />
          </Box>
        </Section>
        <DocumentSections
          countryCode={destinationCountry}
          documentRequired={isDocumentRequired}
          creatingTransaction={creatingTransaction}
          simulation={simulation}
          attachment={attachment}
          transferPurpose={transferPurpose}
          paymentReference={paymentReference}
          error={
            error === FormErrors.MISSING_SUPPORTING_DOCUMENTATION
              ? tr('REQUIRED_SUPPORTING_DOCUMENTATION')
              : null
          }
          paymentReferenceError={
            error === FormErrors.PAYMENT_REFERENCE_INVALID_CHARACTERS
              ? tr('VALIDATION_INVALID_SWIFT_CHARACTERS', tr('RECIPIENT_MEMO'))
              : null
          }
          onPaymentReferenceChange={handlePaymentReferenceChange}
          onPurposeChange={handlePurposeChange}
          onFileChange={handleFileChange}
          onFileRemove={handleFileRemove}
        />
        <Box width={552} mx="auto">
          <LoadingButton
            variant="contained"
            color="primary"
            fullWidth={true}
            disabled={confirmDisabled}
            loading={mutation.status === QueryStatus.pending}
            onClick={handleConfirm}>
            {tr('CONFIRM_PAYMENT')}
          </LoadingButton>
        </Box>
      </FullScreenModalContainer>

      <Outlet context={reducer} />
    </ApplicationDialog>
  );
};

const emptyAmount: NotNullable<Amount> = {
  value: 0,
  currencyCode: CurrencyCodeType.UNSET,
};

const emptyTransactionDetails: NfTransactionDetailsResponse = {
  conversionDetails: {
    clientRate: 0,
    effectiveTransactionValue: 0,
    marketExchangeRate: 0,
    calizaRate: 0,
    amountToBeConverted: emptyAmount,
  },
  feeDetails: {
    totalFees: emptyAmount,
    integratorFee: emptyAmount,
    taxes: emptyAmount,
    fixedFees: [],
    oldFees: [],
    percentageFees: [],
    spreadFees: [],
    vet: '0.0000',
  },
  expectedSettlementTimeDetails: null,
};

const StyledAccordion = styled(Accordion)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    border: 'none',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.custom.DividerLight,
    borderRadius: 16,
    '&::before': {
      display: 'none',
    },
    '&:last-of-type': {
      borderRadius: 16,
    },
  }),
);

const styles: Record<string, SystemStyleObject> = {
  expectedSettlementTimeInfo: {
    display: 'flex',
    justifyContent: 'center',
    mt: 3,
    color: 'primary.main',
  },
};

const getToRailFromAccount = (
  contacts: readonly Contact[],
  recipientAccountId: string,
): PaymentRailType => {
  const accounts =
    contacts
      .map((contact: Contact): readonly FiatAccount[] => {
        return contact.accounts;
      }, [])
      ?.flat() ?? [];
  const selectedAccount = accounts.find(
    (account: FiatAccount): boolean => account.id === recipientAccountId,
  );
  if (!selectedAccount) {
    return PaymentRailType.unset;
  }

  switch (selectedAccount.fiatAccountType) {
    case FiatAccountType.ach:
      return PaymentRailType.ACH;
    case FiatAccountType.swift:
      return PaymentRailType.Swift;
    case FiatAccountType.wire:
      return PaymentRailType.Wire;
    case FiatAccountType.pix:
      return PaymentRailType.PIX;
    case FiatAccountType.cop:
      return PaymentRailType.COP;
    case FiatAccountType.spei:
      return PaymentRailType.SPEI;
    case FiatAccountType.cvu:
      return PaymentRailType.CVU;
    case FiatAccountType.unset:
      return PaymentRailType.unset;
  }
};

const getToCurrencyFromAccount = (
  contacts: readonly Contact[],
  recipientAccountId: string,
): CurrencyCodeType => {
  const accounts =
    contacts
      .map((contact: Contact): readonly FiatAccount[] => {
        return contact.accounts;
      }, [])
      ?.flat() ?? [];
  const selectedAccount = accounts.find(
    (account: FiatAccount): boolean => account.id === recipientAccountId,
  );
  if (!selectedAccount) {
    return CurrencyCodeType.UNSET;
  }

  return getAccountCurrency(selectedAccount.accountDetails);
};

const resetAmountRelatedErrors = (error: FormErrors | null): FormErrors | null => {
  if (error === FormErrors.MISSING_AMOUNT || error === FormErrors.AMOUNT_BIGGER_THAN_BALANCE) {
    return null;
  }

  return error;
};

const getBankCountryFromAccountDetails = (accountDetails: AccountDetails): CountryType | null => {
  return match(accountDetails)
    .when(isSwiftAccountDetails, (swiftAccountDetails: SwiftAccountDetails): CountryType | null => {
      const {
        swiftDetails: {
          bankInfo: { country },
        },
      } = swiftAccountDetails;

      if (isCountryType(country)) {
        return country;
      }
      console.debug('cannot determine the country for accountDetails', accountDetails);

      return null;
    })
    .otherwise((): null => null);
};
